import type { FC, RefObject } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useSelector } from "react-redux";

import { Flex } from "@similarweb/ui-components/foundations";

import type { TimeGranularitySymbol } from "app/common/utils/timeGranularity";
import type ColorStack from "app/components/colorsStack/ColorStack";
import type { ILegendItems } from "app/components/React/Legends/legendsTypes";
import { parseTableRawData } from "app/pages/industry-analysis/players/market-shares/utils/parseTableRawData";
import type { ISegmentsModule } from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { EChartDisplayType } from "pro-features/components/Chart/src/configs/column-chart/columnChartConfig";

import { MarketSharesChart } from "./MarketSharesChart";
import { MarketSharesChartLegends } from "./MarketSharesChartLegends";
import { RegionalsCountryDomainsModal } from "./RegionalsCountryDomainsModal";
import type { EChartType, RegionalDomainData, SwitchGraphCompareData } from "./types";
import { getOthersDomainName } from "./utils/const";
import { parseLegendsRawData, parseRawData } from "./utils/parseRawData";
import { getApiKey, shouldDisplayOthers, shouldDisableOthers } from "./utils/utils";

type ModalDomainData = {
    parentDomainName: string | null;
    domains: RegionalDomainData[] | null;
};

interface IMarketSharesChartContainerProps {
    chartType: EChartType;
    chartRef: React.RefObject<HTMLDivElement>;
    chartDisplayType: EChartDisplayType;
    rawData: SwitchGraphCompareData;
    setDashboardKeys: (value: ILegendItems) => void;
    legendItems: ILegendItems;
    setLegendItems: (items: ILegendItems) => void;
    previousLegendItems: ILegendItems;
    colorStack: RefObject<ColorStack>;
    timeGranularity: TimeGranularitySymbol;
    onLegendsChange: (items: ILegendItems) => void;
    onBeforeLegendItemsCreate: () => void;
    isMonthToDate: boolean;
    isDaily: boolean;
}

export const MarketSharesChartContainer: FC<IMarketSharesChartContainerProps> = ({
    chartDisplayType,
    chartType,
    chartRef,
    rawData,
    setDashboardKeys,
    legendItems,
    setLegendItems,
    previousLegendItems,
    colorStack,
    timeGranularity,
    onLegendsChange,
    onBeforeLegendItemsCreate,
    isMonthToDate,
    isDaily,
}) => {
    const data = parseRawData(rawData.Data);
    const compareData = parseRawData(rawData.CompareData);
    const {
        params: { webSource },
    } = useCurrentStateAndParams();
    const enrichedData = parseTableRawData(rawData.Data, webSource);
    const isPercentage = chartDisplayType === EChartDisplayType.PERCENTAGE;
    const [isDomainsModalOpen, setIsDomainsModalOpen] = useState(false);
    const [modalDomainData, setModalDomainData] = useState<ModalDomainData>({
        parentDomainName: null,
        domains: null,
    });

    const segmentsModule: ISegmentsModule = useSelector((state) => {
        const { segmentsModule } = state;
        return segmentsModule;
    });
    const getUserAndAccountSegmentById = useMemo(
        (): any => SegmentsUtils.getUserAndAccountSegmentByIdHof(segmentsModule),
        [],
    );
    const ApiDisplayType = getApiKey(isPercentage ? EChartDisplayType.PERCENTAGE : EChartDisplayType.ABSOLUTE);

    const handleViewCountryDomains = useCallback((parentDomainName: string, domains: RegionalDomainData[]) => {
        setModalDomainData({
            parentDomainName,
            domains,
        });
        setIsDomainsModalOpen(true);
    }, []);

    const handleModalClose = useCallback(() => {
        setModalDomainData({
            parentDomainName: null,
            domains: null,
        });
        setIsDomainsModalOpen(false);
    }, []);

    const handleLegendsChange = useCallback(
        (items: ILegendItems) => {
            // Find non-Others items
            const othersDomainName = getOthersDomainName();
            const nonOthersItems = items.filter((item) => item.name !== othersDomainName);
            const totalAvailableDomainCount = nonOthersItems.length;
            const visibleDomainCount = nonOthersItems.filter((item) => item.visible).length;

            let updatedItems = items;
            // If all non-Others items are checked, uncheck Others
            if (visibleDomainCount === totalAvailableDomainCount) {
                updatedItems = items.map((item) =>
                    item.name === othersDomainName ? { ...item, visible: false } : item,
                );
            }
            onLegendsChange(updatedItems);
        },
        [onLegendsChange],
    );

    const { shouldShowOthers, shouldDisableOthersCheckbox } = useMemo(() => {
        const othersDomainName = getOthersDomainName();
        const nonOthersItems = legendItems.filter((item) => item.name !== othersDomainName);
        const visibleDomainCount = nonOthersItems.filter((item) => item.visible).length;
        const totalAvailableDomainCount = nonOthersItems.length;

        const othersItem = legendItems.find((item) => item.name === othersDomainName);
        const isOthersVisible = othersItem?.visible ?? false;

        // Don't show Others if all available domains are selected (even if Others is checked)
        const allDomainsAreSelected = visibleDomainCount >= totalAvailableDomainCount;

        return {
            shouldShowOthers: !allDomainsAreSelected && shouldDisplayOthers(isPercentage, isOthersVisible),
            shouldDisableOthersCheckbox: shouldDisableOthers(visibleDomainCount, totalAvailableDomainCount),
        };
    }, [isPercentage, legendItems]);

    useEffect(() => {
        onBeforeLegendItemsCreate();

        const newLegendItems = parseLegendsRawData(
            data[ApiDisplayType],
            enrichedData,
            chartDisplayType,
            colorStack,
            previousLegendItems,
            getUserAndAccountSegmentById,
            handleViewCountryDomains,
        );

        setLegendItems(newLegendItems);
        setDashboardKeys(newLegendItems);
    }, [rawData, chartDisplayType, isMonthToDate]);

    return (
        <>
            <Flex
                ref={chartRef}
                padding="16px"
                width="100%"
                gap="16px"
                alignItems="flex-start"
                data-automation="market-shares-chart-container"
            >
                <MarketSharesChart
                    chartType={chartType}
                    chartDisplayType={chartDisplayType}
                    parsedData={data}
                    compareData={compareData}
                    enrichedData={enrichedData}
                    rawDomainData={rawData.Data}
                    legendItems={legendItems}
                    timeGranularity={timeGranularity}
                    isMonthToDate={isMonthToDate}
                    showOthers={shouldShowOthers}
                />
                <MarketSharesChartLegends
                    chartDisplayType={chartDisplayType}
                    legendItems={legendItems}
                    getLegendColor={() => colorStack.current.acquire()}
                    isDaily={isDaily}
                    onLegendsChange={handleLegendsChange}
                    disableOthers={shouldDisableOthersCheckbox}
                />
            </Flex>
            <RegionalsCountryDomainsModal
                parentDomainName={modalDomainData.parentDomainName}
                domains={modalDomainData.domains}
                isOpen={isDomainsModalOpen}
                onClose={handleModalClose}
                timeGranularity={timeGranularity}
            />
        </>
    );
};
